// client/src/pages/AuctionPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import Countdown from '../components/Countdown';
import {
  Container, Grid, Card, CardActionArea, CardMedia, CardContent, Typography, Chip, Box,
  CircularProgress, Alert,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import { formatCurrency } from '../utils/currency';
import { getImageUrl, API_BASE_URL } from '../utils/media';
import EmptyState from '../components/EmptyState';

const AuctionPage = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socket = useSocket();

  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auctions`);
        if (!response.ok) throw new Error('Failed to fetch auctions.');
        setAuctions(await response.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAuctions();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleAuctionUpdate = (updatedAuction) => {
      setAuctions((prev) => prev.map((a) => (a.id === updatedAuction.id ? { ...a, current_price: updatedAuction.currentPrice } : a)));
    };
    const handleAuctionEnded = ({ id }) => {
      setAuctions((prev) => prev.map((a) => (a.id === id ? { ...a, status: 'ended' } : a)));
    };

    socket.on('auctionUpdate', handleAuctionUpdate);
    socket.on('auctionEnded', handleAuctionEnded);
    return () => {
      socket.off('auctionUpdate', handleAuctionUpdate);
      socket.off('auctionEnded', handleAuctionEnded);
    };
  }, [socket]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;

  const liveAuctions = auctions.filter((a) => a.status !== 'ended' && a.status !== 'sold');

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Live Auctions</Typography>
        <Typography color="text.secondary">
          Bid in real time on items up for auction. Prices update instantly for everyone watching.
        </Typography>
      </Box>

      {liveAuctions.length === 0 ? (
        <EmptyState
          icon={<GavelIcon />}
          title="No live auctions right now"
          description="Check back soon, or list your own item if you're a seller."
        />
      ) : (
        <Grid container spacing={3}>
          {liveAuctions.map((auction) => (
            <Grid item xs={12} sm={6} md={4} key={auction.id}>
              <Card>
                <CardActionArea component={Link} to={`/auctions/${auction.id}`}>
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia component="img" image={getImageUrl(auction.imageUrl)} alt={auction.product_name} sx={{ height: 200, objectFit: 'cover' }} />
                    <Chip
                      icon={<GavelIcon sx={{ fontSize: 16 }} />}
                      label="Live"
                      color="error"
                      size="small"
                      sx={{ position: 'absolute', top: 10, left: 10, fontWeight: 700 }}
                    />
                    {auction.buy_now_price && (
                      <Chip label="Buy It Now" color="success" size="small" sx={{ position: 'absolute', top: 10, right: 10, fontWeight: 700 }} />
                    )}
                  </Box>
                  <CardContent>
                    {auction.category && (
                      <Chip label={auction.subcategory ? `${auction.category} / ${auction.subcategory}` : auction.category} size="small" sx={{ mb: 1 }} />
                    )}
                    <Typography variant="subtitle1" fontWeight={600} noWrap>{auction.product_name}</Typography>
                    <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
                      {formatCurrency(auction.current_price)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Ends in: <Countdown endTime={auction.end_time} />
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default AuctionPage;
